import { useState } from "react";
import { vehicleDisplayIdentifier } from "../../utils/vehicleIdentifier";

import { DateTime } from "luxon";
import { useDrag } from "react-dnd";
import { useTranslation } from "react-i18next";

import {
  BoltIcon,
  BriefcaseIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  CurrencyPoundIcon,
  ExclamationTriangleIcon,
  TruckIcon,
  WrenchScrewdriverIcon,
} from "@heroicons/react/24/outline";

import useNotification from "../notifications/useNotifications";
import Tooltip from "../shared/Tooltip";
import Avatar from "../user/Avatar";
import useApiHelper from "../../hooks/useApiHelper";
import { useSignalR } from "../../contexts/SignalRContext";
import { DragTypes } from "../../models/DragTypes";
import { IAllJobsResponse } from "../../models/Job";
import {
  IPlannerDragItem,
  IPlannerVehicleRow,
  PlannerColourBy,
} from "../../models/ProductionPlanner";
import classNames from "../../utils/classNames";
import openInNewTab from "../../utils/openInNewTab";
import { isPlannerVehicleComplete } from "../../utils/plannerVehicleProgress";
import PriceFormat from "../../utils/priceFormat";
import {
  ageBorderClasses,
  complexityBorderClasses,
  complexityChipClasses,
  complexityLabel,
  daysOnSite,
  lineChipClasses,
  lineLabel,
} from "../../utils/vehicleDisplay";

interface PlannerVehicleCardProps {
  vehicle: IPlannerVehicleRow;
  colourBy: PlannerColourBy;
  onMoveUp?: () => void;
  onMoveDown?: () => void;
  canMoveUp?: boolean;
  canMoveDown?: boolean;
  // Set while a search is running: matches get a ring, the rest fade back so the
  // matches are easy to pick out of a full planner.
  isSearchMatch?: boolean;
  isSearchDimmed?: boolean;
}

export default function PlannerVehicleCard({
  vehicle,
  colourBy,
  onMoveUp,
  onMoveDown,
  canMoveUp,
  canMoveDown,
  isSearchMatch = false,
  isSearchDimmed = false,
}: PlannerVehicleCardProps) {
  const { t } = useTranslation("production");
  const { get } = useApiHelper();
  const { addNotification } = useNotification();
  const { activeJobSessionsByVehicle } = useSignalR();
  const [isOpeningJob, setIsOpeningJob] = useState(false);

  const activeJobUsers = activeJobSessionsByVehicle[vehicle.id] ?? [];

  const days = daysOnSite(vehicle.arrivalDate, vehicle.collectedOn);

  const fuel = (vehicle.fuelType ?? "").toLowerCase();
  const isHybrid = fuel.includes("hybrid");
  const isElectric = !isHybrid && (fuel.includes("electric") || fuel.includes("electricity"));
  const isHighValue = (vehicle.partsListValue ?? 0) > 1000;
  const isConvertible = (vehicle.bodyStyle ?? "").toLowerCase().includes("convertible");
  const isFourByFour = vehicle.driveType === "4x4";
  const showHazard = isConvertible || isFourByFour;
  const hazardLabel = isConvertible && isFourByFour
    ? t("Convertible & 4x4")
    : isConvertible
      ? t("Convertible")
      : t("4x4");

  const dragItem: IPlannerDragItem = {
    vehicleId: vehicle.id,
    currentBayId: vehicle.dismantlingBayId,
    currentPlannedOn: vehicle.plannedDismantlingOn,
  };

  const [{ isDragging }, drag] = useDrag(
    () => ({
      type: DragTypes.PLANNER_VEHICLE,
      item: dragItem,
      collect: (monitor) => ({
        isDragging: monitor.isDragging(),
      }),
    }),
    [vehicle.id, vehicle.dismantlingBayId, vehicle.plannedDismantlingOn]
  );

  const handleOpenVehicle = (e: React.MouseEvent) => {
    e.stopPropagation();
    openInNewTab(`/vehicles/${vehicle.id}`);
  };

  const handleOpenJob = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (isOpeningJob) return;

    setIsOpeningJob(true);
    try {
      const response = await get<IAllJobsResponse>(`/job/vehicle/${vehicle.id}`);
      const jobs = response.data ?? [];
      const latest = [...jobs].sort(
        (a, b) => new Date(b.created).getTime() - new Date(a.created).getTime()
      )[0];

      if (!latest) {
        addNotification({
          variant: "warn",
          primaryText: t("No job found for this vehicle"),
        });
        return;
      }

      openInNewTab(`/jobs/${latest.id}`);
    } catch {
      addNotification({
        variant: "error",
        primaryText: t("Unable to load job for this vehicle"),
      });
    } finally {
      setIsOpeningJob(false);
    }
  };

  const isComplete = isPlannerVehicleComplete(vehicle);

  // A vehicle scheduled for a day now in the past that still isn't finished is
  // overdue — flag it amber so it stands out as needing rebooking.
  const isOverdue =
    !isComplete &&
    vehicle.plannedDismantlingOn !== null &&
    DateTime.fromISO(vehicle.plannedDismantlingOn, { zone: "utc" }).startOf("day") <
    DateTime.local().startOf("day");

  const borderClass =
    colourBy === "Age"
      ? ageBorderClasses(days)
      : complexityBorderClasses(vehicle.complexity);

  const iconButtonClasses =
    "inline-flex h-6 w-6 items-center justify-center rounded border border-gray-200 bg-white text-gray-500 hover:bg-gray-50 hover:text-gray-700 disabled:opacity-50";

  return (
    <div
      ref={drag}
      data-planner-vehicle={vehicle.id}
      className={classNames(
        "rounded-md border shadow-sm px-2.5 py-2 cursor-grab select-none transition-opacity",
        isComplete
          ? "bg-green-50 border-green-200"
          : isOverdue
            ? "bg-orange-50 border-orange-200"
            : "bg-white border-gray-200",
        borderClass,
        isSearchMatch ? "ring-2 ring-primary-500 ring-offset-1 shadow-md" : "",
        isSearchDimmed ? "opacity-40" : "",
        isDragging ? "opacity-40" : "hover:shadow"
      )}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-x-1.5">
            <span className="text-sm font-semibold text-gray-900 truncate">
              {vehicleDisplayIdentifier(vehicle) || t("No VRM")}
            </span>
            {isElectric && (
              <Tooltip content={t("Electric")}>
                <BoltIcon className="h-3.5 w-3.5 text-yellow-500" />
              </Tooltip>
            )}
            {isHybrid && (
              <Tooltip content={t("Hybrid")}>
                <BoltIcon className="h-3.5 w-3.5 text-green-600" />
              </Tooltip>
            )}
            {isHighValue && (
              <Tooltip
                content={t("Parts list worth {{value}}", {
                  value: PriceFormat(vehicle.partsListValue),
                })}
              >
                <span className="inline-flex h-4 w-4 items-center justify-center rounded-full border border-yellow-500 text-yellow-600">
                  <CurrencyPoundIcon className="h-3 w-3" />
                </span>
              </Tooltip>
            )}
            {showHazard && (
              <Tooltip content={hazardLabel}>
                <ExclamationTriangleIcon className="h-3.5 w-3.5 text-red-600" />
              </Tooltip>
            )}
          </div>
          <div className="text-xs text-gray-700 truncate">
            {[vehicle.combinedMake, vehicle.combinedModel].filter(Boolean).join(" ")}
            {vehicle.bodyStyle && (
              <span className="ml-1 text-gray-500">{vehicle.bodyStyle}</span>
            )}
          </div>
          <div className="flex items-center gap-x-1 text-[11px] text-gray-500">
            {vehicle.partsTotal === 1
              ? t("{{removed}}/{{total}} part", {
                removed: vehicle.partsRemoved,
                total: vehicle.partsTotal,
              })
              : t("{{removed}}/{{total}} parts", {
                removed: vehicle.partsRemoved,
                total: vehicle.partsTotal,
              })}

            <span
              className={classNames(
                "text-[10px] font-medium rounded px-1 py-0 max-w-full truncate",
                lineChipClasses(vehicle.productionLine)
              )}
            >
              {lineLabel(vehicle.productionLine)}
            </span>
          </div>
        </div>
        <div className="flex flex-col items-end gap-y-1 shrink-0">
          <span
            className={classNames(
              "text-[10px] font-semibold uppercase tracking-wide rounded px-1.5 py-0.5",
              complexityChipClasses(vehicle.complexity)
            )}
          >
            {t(complexityLabel(vehicle.complexity))}
          </span>
          <div className="flex items-center gap-x-1">
            <button
              type="button"
              onClick={handleOpenVehicle}
              className={iconButtonClasses}
              title={t("Open vehicle")}
            >
              <TruckIcon className="h-3.5 w-3.5" />
            </button>
            <button
              type="button"
              onClick={handleOpenJob}
              disabled={isOpeningJob}
              className={iconButtonClasses}
              title={t("Open job")}
            >
              <BriefcaseIcon className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>
      </div>

      <div className="flex items-end justify-between gap-x-1.5">
        <div className="min-w-0 flex flex-col items-start">
          {vehicle.locationName && (
            <span className="text-[11px] text-gray-500 max-w-full truncate">
              {t("Location: {{name}}", { name: vehicle.locationName })}
            </span>
          )}
          {days !== null && (
            <span className="text-[11px] text-gray-500">
              {t("{{days}}d on site", { days })}
            </span>
          )}
        </div>

        {(onMoveUp || onMoveDown) && (
          <div className="flex items-center gap-x-1 shrink-0">
            <button
              type="button"
              onMouseDown={(e) => e.stopPropagation()}
              onClick={(e) => {
                e.stopPropagation();
                onMoveUp?.();
              }}
              disabled={!canMoveUp}
              className={iconButtonClasses}
              title={t("Move up")}
            >
              <ChevronUpIcon className="h-3.5 w-3.5" />
            </button>
            <button
              type="button"
              onMouseDown={(e) => e.stopPropagation()}
              onClick={(e) => {
                e.stopPropagation();
                onMoveDown?.();
              }}
              disabled={!canMoveDown}
              className={iconButtonClasses}
              title={t("Move down")}
            >
              <ChevronDownIcon className="h-3.5 w-3.5" />
            </button>
          </div>
        )}
      </div>

      {activeJobUsers.length > 0 && (
        <div className="mt-1.5 flex items-center gap-1">
          <WrenchScrewdriverIcon className="w-3.5 h-3.5 text-amber-500 shrink-0" />
          <div className="flex">
            {activeJobUsers.map((userId, key) => (
              <Avatar key={key} userId={userId} classes="w-6 h-6 rounded-full [&:not(:first-child)]:-ml-1.5" textClasses="text-[15px]" />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
